import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useApolloClient, useQuery } from "@apollo/client";
import type { Row } from "@tanstack/react-table";
import ListItem from "@/components/ListItem";
import EmptyState from "@/components/EmptyState";
import ErrorBoundary from "@/components/ErrorBoundary";
import SideSheetModal from "@/components/SideSheetModal";
import Table, { TableHeading } from "@/components/Table";
import { useCanManageGrpCommissionBool } from "@/hooks/useCapabilities";
import { toUTCFormat } from "@/lib/dateHelpers";
import { useNavigate } from "@/router";
import { COMMISSION_CAMPAIGNS_QUERY } from "@/pages/billing/finance/commission/campaigns/queries";
import { COMMISSION_PROGRAM_CONSENTS_QUERY } from "@/pages/billing/finance/commission/consents/queries";
import type { CommissionProgramConsentNode } from "@/pages/billing/finance/commission/consents/types";
import { formatEnumLabel } from "@/pages/billing/finance/commission/_utils";
import { isCommissionMocksEnabled } from "@/pages/billing/finance/commission/_mocks/commissionMockLink";
import { personGrpIaaSummaryByPgaId } from "@/pages/billing/finance/commission/_mocks/personGrpFixtures";
import { useShowCommissionPersonGrpTab } from "../_utils";
import { GRP_COMMISSION_CAMPAIGNS_LIMIT } from "./constants";
import { PERSON_GRP_CONTEXT_QUERY } from "./queries";
import { getFaaColumns } from "./_components/FaaTableColumns";
import { getIaaColumns } from "./_components/IaaTableColumns";
import type { PersonGrpFaaRow, PersonGrpIaaRow } from "./types";

export default function PersonGrpBenefitsTab({ stacked = false }: { stacked?: boolean }) {
  const { pgaId } = useParams();
  const showGrpTab = useShowCommissionPersonGrpTab();
  const canManageCommission = useCanManageGrpCommissionBool();
  const navigate = useNavigate();
  const topSpacingClass = stacked ? undefined : "mt-12";
  const client = useApolloClient();
  const iaaColumns = useMemo(() => getIaaColumns(), []);
  const [faaRows, setFaaRows] = useState<PersonGrpFaaRow[]>([]);
  const [consentsLoading, setConsentsLoading] = useState(false);
  const [consentsError, setConsentsError] = useState<Error | null>(null);
  const [selectedFaaRow, setSelectedFaaRow] = useState<PersonGrpFaaRow | null>(null);
  const faaColumns = useMemo(() => getFaaColumns({ onViewClick: setSelectedFaaRow }), []);

  const { data: personData, error: personError } = useQuery(PERSON_GRP_CONTEXT_QUERY, {
    variables: { pgaId: pgaId! },
    skip: !pgaId || !showGrpTab
  });

  const {
    data: campaignsData,
    loading: campaignsLoading,
    error: campaignsError
  } = useQuery(COMMISSION_CAMPAIGNS_QUERY, {
    variables: { first: GRP_COMMISSION_CAMPAIGNS_LIMIT },
    skip: !showGrpTab
  });

  const crmPersonId = personData?.person.id;
  const campaignNodes = useMemo(() => campaignsData?.commissionCampaigns.nodes ?? [], [campaignsData]);

  useEffect(() => {
    if (!crmPersonId || campaignNodes.length === 0) {
      setFaaRows([]);
      setConsentsError(null);
      return;
    }

    let cancelled = false;
    setConsentsLoading(true);
    setConsentsError(null);

    // The consents query requires a campaign ID, so one query per campaign is
    // unavoidable. Settle all of them: keep rows from campaigns that succeed
    // and surface failures instead of silently emptying the FAA table.
    Promise.allSettled(
      campaignNodes.map((campaign) =>
        client
          .query({
            query: COMMISSION_PROGRAM_CONSENTS_QUERY,
            variables: { campaignId: campaign.id, crmPersonId, first: 20 }
          })
          .then((result) => ({
            campaign,
            nodes: (result.data?.commissionProgramConsents.nodes ?? []) as CommissionProgramConsentNode[]
          }))
      )
    )
      .then((results) => {
        if (cancelled) return;

        const rows: PersonGrpFaaRow[] = [];
        let firstError: unknown = null;
        for (const result of results) {
          if (result.status === "fulfilled") {
            for (const consent of result.value.nodes) {
              rows.push({
                ...consent,
                id: `${result.value.campaign.id}-${consent.id}`,
                partnerName: result.value.campaign.partner.name
              });
            }
          } else if (firstError === null) {
            firstError = result.reason;
          }
        }

        setFaaRows(rows);
        setConsentsError(
          firstError === null
            ? null
            : firstError instanceof Error
              ? firstError
              : new Error("Failed to load FAA records.")
        );
      })
      .finally(() => {
        if (!cancelled) {
          setConsentsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [campaignNodes, client, crmPersonId]);

  if (!showGrpTab) {
    return (
      <EmptyState
        title="GRP unavailable"
        caption="Commission GRP benefits are not enabled for your account."
        iconName="lock"
        className={topSpacingClass}
      />
    );
  }

  const queryError = personError ?? campaignsError;
  if (queryError) {
    return (
      <ErrorBoundary
        variant="inline"
        title="Failed to Load GRP Benefits"
        message={queryError.message}
        className={topSpacingClass}
      />
    );
  }

  // IAA fixtures are dev-mock only. Until a real IAA query exists, non-mock
  // environments render the empty IAA table.
  const iaaSummary = pgaId && isCommissionMocksEnabled() ? (personGrpIaaSummaryByPgaId[pgaId] ?? null) : null;
  const iaaRows: PersonGrpIaaRow[] = iaaSummary
    ? [{ ...iaaSummary, id: `${iaaSummary.receiverPgaId}-${iaaSummary.effectiveDate}` }]
    : [];

  return (
    <div>
      <TableHeading
        heading="Facility Authorization Agreements (FAA)"
        body="The annual agreement where this member names the sponsors they'll receive contributions from and confirms their ownership status. Sponsor contributions can't be deposited without an active FAA."
        actions={
          canManageCommission && pgaId
            ? [
                {
                  icon: "add",
                  label: "Add Authorization(s)",
                  variant: "filled",
                  onClick: () => navigate("/people/:pgaId/benefits/grp/faa/new", { params: { pgaId } }),
                  testId: "add-faa-button"
                }
              ]
            : undefined
        }
      />
      {consentsError && faaRows.length === 0 && !consentsLoading ? (
        <ErrorBoundary variant="inline" title="Failed to Load FAA Records" message={consentsError.message} />
      ) : (
        <>
          {consentsError ? (
            <ErrorBoundary variant="inline" title="Some FAA Records Failed to Load" message={consentsError.message} />
          ) : null}
          <Table
            data={faaRows}
            columns={faaColumns}
            loading={campaignsLoading || consentsLoading}
            onClickRow={(row: Row<PersonGrpFaaRow>) => setSelectedFaaRow(row.original)}
            renderNoDataEmptyState={() => (
              <EmptyState
                title="No FAA on File"
                caption="Facility authorization agreements will appear here when recorded for this member."
                iconName="description"
              />
            )}
          />
        </>
      )}

      <SideSheetModal
        isSidebarOpen={selectedFaaRow != null}
        setSidebarOpen={(open) => {
          if (!open) setSelectedFaaRow(null);
        }}
        title="FAA Details"
        actions={null}
      >
        {selectedFaaRow ? (
          <div className="w-full space-y-4 pt-2.5" data-testid="faa-detail-side-sheet">
            <ListItem iconName="handshake" iconBackground overline="Sponsor" headline={selectedFaaRow.partnerName} />
            <ListItem
              iconName="info"
              iconBackground
              overline="Status"
              headline={formatEnumLabel(selectedFaaRow.status)}
            />
            <ListItem
              iconName="account_tree"
              iconBackground
              overline="Attribution"
              headline={formatEnumLabel(selectedFaaRow.attributionModel)}
            />
            <ListItem
              iconName="badge"
              iconBackground
              overline="Plan Option"
              headline={selectedFaaRow.ownership ? formatEnumLabel(selectedFaaRow.ownership) : "—"}
            />
            <ListItem
              iconName="today"
              iconBackground
              overline="Effective Date"
              headline={toUTCFormat(selectedFaaRow.startsAt)}
            />
            <ListItem
              iconName="event"
              iconBackground
              overline="Expiration Date"
              headline={selectedFaaRow.endsAt ? toUTCFormat(selectedFaaRow.endsAt) : "—"}
            />
          </div>
        ) : null}
      </SideSheetModal>

      <TableHeading heading="Individual Allocation Agreement (IAA)" />
      <Table
        data={iaaRows}
        columns={iaaColumns}
        loading={false}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No IAA on File"
            caption="Individual allocation agreements will appear here when recorded for this member."
            iconName="description"
          />
        )}
      />
    </div>
  );
}
